import styled from "@emotion/styled";
import { Countdown, NoBreak, Stack, Text } from "atoms";
import { OpenCallTag } from "../types";

type OpenCallDetailsHeadingProps = {
  communityVotingEndDate?: string;
  communityVotingStartDate?: string;
  isEligible: boolean;
  numberSubmitted: number;
  refetchOpenCall: VoidFunction;
  remainingSubmissions: number;
  tag: OpenCallTag;
  userIsWinner: boolean | undefined;
  canPurchaseSubmissions: boolean;
  userVoted: boolean;
  submissionStartDate: string;
  submissionEndDate?: string;
};

function OpenCallDetailsHeading({
  numberSubmitted,
  isEligible,
  canPurchaseSubmissions,
  refetchOpenCall,
  remainingSubmissions,
  tag,
  userVoted,
  userIsWinner,
  submissionEndDate,
  communityVotingStartDate,
  communityVotingEndDate,
  submissionStartDate,
}: OpenCallDetailsHeadingProps) {
  // Open call is over, artist are selected
  if (["Artists Selected", "On Display", "Concluded"].includes(tag)) {
    return (
      <Stack>
        <Text bold font="display" size="md">
          <BlackText>
            {userIsWinner ? "Congratulations!" : "This Open Call Has Concluded"}
          </BlackText>
        </Text>
        <Text size="xs">
          {userIsWinner && <span>Your artwork has been selected! </span>}
          <span>
            Selected artists have been notified via email, and are displayed
            below.
          </span>
        </Text>
      </Stack>
    );
  }
  if (tag === "Under Curation") {
    return (
      <Stack>
        <Text bold font="display" size="lg">
          <BlackText>Curation is Underway</BlackText>
        </Text>
        <Text size="xs">
          Selected artists will be notified via email and displayed here once
          curation is complete.
        </Text>
      </Stack>
    );
  }

  let pre: React.ReactNode;
  let end = submissionEndDate;
  let text = "";
  let isEndDate = true;

  if (tag === "Submissions Open") {
    if (numberSubmitted === 0) {
      /** User has not submitted yet */
      pre = "Submit Your Artwork";
      text = "Remaining";
    } else if (!isEligible) {
      /** User has submitted at least 1 but is not eligible */
      pre = "Submissions Close in";
    } else if (numberSubmitted === 1 && canPurchaseSubmissions) {
      /** User has submitted at least 1 and can purchase more */
      pre = "You're In! Want to submit more?";
      text = "Remaining";
    } else if (!remainingSubmissions) {
      /** User has no more submissions, regardless of purchasing */
      pre = `Thank You For Your Submission${numberSubmitted === 1 ? "" : "s"}`;
      end = undefined;
    } else {
      /** User has 1 or 2 remaining submissions */
      pre = `You Can Submit ${remainingSubmissions} Additional Artwork${
        remainingSubmissions === 1 ? "" : "s"
      }`;
      text = "Remaining";
    }
  }

  if (tag === "Voting Soon") {
    pre = "Voting Starts In";
    end = communityVotingStartDate;
    isEndDate = false;
  }
  if (tag === "Voting Open") {
    pre = userVoted ? <>Thanks For Your Vote!</> : "Cast Your Vote";
    end = communityVotingEndDate;
    text = userVoted ? "Until Voting Ends" : "Remaining";
  }
  if (tag === "Coming Soon") {
    pre = "Submissions Open In";
    end = submissionStartDate;
    isEndDate = false;
  }

  return (
    <Stack>
      <Text bold font="display" size="lg">
        {pre}

        {end && (
          <>
            <br />
            <NoBreak>
              <BlackText>
                <Countdown
                  date={new Date(end)}
                  showSeconds
                  textAfter={text}
                  isEndDate={isEndDate}
                  onComplete={refetchOpenCall}
                />
              </BlackText>
            </NoBreak>
          </>
        )}
      </Text>
    </Stack>
  );
}
const BlackText = styled.span(({ theme }) => ({
  color: theme.colors.fg,
}));

export { OpenCallDetailsHeading };
